import { RefObject, SyntheticEvent, useRef, useState } from "react";
import { Fab, IconButton, InputBase, Stack } from "@mui/material";
import { ArrowUpwardRounded, MicNoneRounded, Stop } from "@mui/icons-material";
import { useCase } from "../../provider/CaseProvider";
import { useAiChat } from "../../provider/AiChatProvider";
import { useAiEventStreamControls, useAiEventStreamSnapshot } from "../../hooks/useAiEventStream";
import { createResource, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelAiChatWorkflow, sendAiChatPrompt } from "../../services/aiChatService";
import { canCancelVoiceRecording } from "../../services/aiChatVoiceInputService";
import { VoiceRecordingStatus } from "../../types/AIChat/AiChatVoiceInput";
import VoiceRecordingBar from "./VoiceRecordingBar";
import AIChatSkillPopper, { AIChatSkillPopperHandle } from "./AIChatSkillPopper";
import { useAiChatVoiceInput } from "./useAiChatVoiceInput";
import aiChatInputStyles from "./aiChatInputStyles";
import ReactGA from "react-ga4";
import { enqueueSnackbar } from "notistack";

const VOICE_BAR_VISIBLE_STATUSES: VoiceRecordingStatus[] = ["recording", "transcribing", "sending"];

type AIChatInputProps = {
  ref: RefObject<HTMLTextAreaElement | null>;
};

export default function AIChatInput({ ref }: AIChatInputProps) {
  const [prompt, setPrompt] = useState("");
  const [isCancellationRequested, setIsCancellationRequested] = useState(false);
  const [isSkillMenuDismissed, setIsSkillMenuDismissed] = useState(false);
  const skillPopperRef = useRef<AIChatSkillPopperHandle>(null);

  const { product } = useCase();
  const { activeThreadId, setActiveThreadId, setIsLoading, isLoading, activeAgent, activeEditStepId } = useAiChat();
  const { connect, reset } = useAiEventStreamControls();
  const { status: streamStatus } = useAiEventStreamSnapshot();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const voice = useAiChatVoiceInput({ prompt, setPrompt, inputRef: ref });

  if (!product) {
    return null;
  }

  const sendPrompt = async (event: SyntheticEvent) => {
    event.preventDefault();
    const submittedPrompt = prompt;
    setPrompt("");
    try {
      await sendAiChatPrompt({
        product,
        activeThreadId,
        setActiveThreadId,
        createMutation,
        queryClient,
        connect,
        reset,
        prompt: submittedPrompt,
        setIsLoading,
        agentType: activeAgent,
        activeExecutionStepId: activeEditStepId,
      });
    } catch {
      setPrompt(submittedPrompt);
      enqueueSnackbar("Die Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }
  };

  const cancelWorkflow = async (threadId: number) => {
    setIsCancellationRequested(true);
    await cancelAiChatWorkflow(threadId);
    setIsCancellationRequested(false);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("ai_chat_messages", { aiChatThread: threadId }),
    });
    reset();
    setIsLoading(false);
  };

  const isVoiceBarVisible = VOICE_BAR_VISIBLE_STATUSES.includes(voice.status);
  const isInputDisabled = isLoading || voice.status === "requestingPermission";
  const stopButtonThreadId = isLoading && streamStatus === "running" ? activeThreadId : null;
  const showStopButton = stopButtonThreadId !== null;

  const voiceBar =
    voice.status !== "idle" || voice.waveform !== null ? (
      <VoiceRecordingBar
        visible={isVoiceBarVisible}
        isTranscribing={voice.status === "transcribing" || voice.status === "sending"}
        canCancel={canCancelVoiceRecording(voice.status)}
        statusMessage={voice.status === "sending" ? "Nachricht wird gesendet …" : "Wird transkribiert …"}
        waveform={voice.waveform}
        onCancel={voice.cancelVoiceRecording}
        onStop={voice.stopAndInsertVoiceRecording}
        onSend={voice.finishVoiceRecording}
      />
    ) : null;

  return (
    <>
      {voiceBar}
      {!isVoiceBarVisible && (
        <form onSubmit={sendPrompt}>
          <AIChatSkillPopper
            ref={skillPopperRef}
            anchorEl={ref.current}
            prompt={prompt}
            disabled={isInputDisabled || activeEditStepId !== null}
            isDismissed={isSkillMenuDismissed}
            onDismiss={() => setIsSkillMenuDismissed(true)}
            onSelect={(command) => {
              setPrompt(`${command} `);
              setIsSkillMenuDismissed(true);
            }}
          />
          <Stack sx={aiChatInputStyles.composer}>
            <InputBase
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
                setIsSkillMenuDismissed(false);
              }}
              fullWidth
              multiline
              disabled={isInputDisabled}
              inputRef={ref}
              maxRows={15}
              placeholder={
                activeEditStepId
                  ? "Änderung für diesen Schritt eingeben..."
                  : `Frage ${activeAgent} zur Akte oder gib Anweisungen...`
              }
              sx={{ padding: "0.5rem 0.75rem 0" }}
              onKeyDown={async (event) => {
                if (skillPopperRef.current?.handleKeyDown(event)) {
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  await sendPrompt(event);
                }
              }}
            />
            <Stack
              direction={"row"}
              spacing={0.5}
              sx={{ alignItems: "center", justifyContent: "flex-end", cursor: "text" }}
              onClick={() => {
                ref.current?.focus();
              }}
            >
              <IconButton
                onClick={() => {
                  ReactGA.event({
                    category: "UserAction",
                    action: "startVoiceRecording",
                  });
                  voice.startVoiceRecording();
                }}
                disabled={isInputDisabled}
              >
                <MicNoneRounded />
              </IconButton>
              <Fab
                type={showStopButton ? "button" : "submit"}
                size={"small"}
                color={"primary"}
                sx={{
                  boxShadow: "none",
                  "&:hover, &:active, &.Mui-focusVisible": { boxShadow: "none" },
                }}
                disabled={showStopButton ? isCancellationRequested : isInputDisabled}
                {...(stopButtonThreadId !== null ? { onClick: () => cancelWorkflow(stopButtonThreadId) } : {})}
              >
                {showStopButton ? <Stop /> : <ArrowUpwardRounded />}
              </Fab>
            </Stack>
          </Stack>
        </form>
      )}
    </>
  );
}
